import { useCallback, useMemo, useState } from 'react'

import { Box, Button, Center, Flex, Grid, Icon, Input, InputGroup, Spinner, Text } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { Project } from '@infraloop/services'
import { LuPlus, LuSearch } from 'react-icons/lu'

import { PROJECT_GRID_COLUMNS } from '../../layout'
import { DEFAULT_SORT, SortKey, filterAndSortProjects, nextSort } from '../../utils'
import ProjectFormModal from './ProjectFormModal'
import ProjectRow from './ProjectRow'
import SortableColumnHeader, { ColumnAlign } from './SortableColumnHeader'

const ProjectTable = ({
  projects,
  isLoading,
  isOwned,
  canCreate,
}: {
  /** Projects to display in the active tab. */
  projects: Project[]
  /** Whether the projects are still loading. */
  isLoading: boolean
  /** Whether these are the user's own projects (enables create + modification affordances). */
  isOwned: boolean
  /** Whether the user's plan allows creating projects (shows the create button). */
  canCreate: boolean
}) => {
  // Hooks
  const { t } = useTranslation()

  // States
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // Derived state
  const visibleProjects = useMemo(() => filterAndSortProjects(projects, search, sort), [projects, search, sort])

  /**
   * Advance the sort state for the clicked column.
   * @param key Column key that was clicked.
   */
  const handleSort = useCallback((key: SortKey) => setSort((s) => nextSort(s, key)), [])

  return (
    <Box>
      <Flex
        justify={isOwned && canCreate ? 'space-between' : 'flex-end'}
        align="center"
        gap="16px"
        flexWrap="wrap"
        mt="18px"
      >
        {isOwned && canCreate && (
          <Button
            colorPalette="blue"
            gap="8px"
            px="16px"
            py="10px"
            h="auto"
            borderRadius="9px"
            fontSize="14px"
            fontWeight="semibold"
            whiteSpace="nowrap"
            onClick={() => setIsCreateOpen(true)}
            data-testid="create-project"
          >
            <Icon as={LuPlus} boxSize="17px" />
            {t('hatsuly.projectList.create')}
          </Button>
        )}
        <InputGroup w="280px" maxW="60vw" startElement={<LuSearch />}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('hatsuly.projectList.searchPlaceholder')}
            borderRadius="9px"
            bg="white"
            data-testid="project-search"
          />
        </InputGroup>
      </Flex>

      <Box
        mt="14px"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="14px"
        boxShadow="0 1px 3px rgba(16,24,40,.06)"
        overflow="hidden"
      >
        {/* header */}
        <Grid
          templateColumns={PROJECT_GRID_COLUMNS}
          alignItems="center"
          columnGap="8px"
          px="16px"
          py="13px"
          bg="#FBFCFE"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Box />
          <SortableColumnHeader
            label={t('hatsuly.columns.name')}
            sortKey={SortKey.Name}
            sort={sort}
            onSort={handleSort}
          />
          <Text
            fontSize="11px"
            fontWeight="bold"
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="gray.500"
            textAlign="center"
          >
            {t('hatsuly.columns.viewers')}
          </Text>
          <SortableColumnHeader
            label={t('hatsuly.columns.size')}
            sortKey={SortKey.Size}
            sort={sort}
            onSort={handleSort}
            align={ColumnAlign.Right}
            pr="20px"
          />
          <SortableColumnHeader
            label={t('hatsuly.columns.updated')}
            sortKey={SortKey.Updated}
            sort={sort}
            onSort={handleSort}
            align={ColumnAlign.Center}
          />
          <SortableColumnHeader
            label={t('hatsuly.columns.created')}
            sortKey={SortKey.Created}
            sort={sort}
            onSort={handleSort}
            align={ColumnAlign.Center}
          />
          <Box />
        </Grid>

        {/* rows */}
        {isLoading && (
          <Center py="40px">
            <Spinner color="primary.500" />
          </Center>
        )}

        {!isLoading &&
          visibleProjects.map((project) => <ProjectRow key={project.project_id} project={project} isOwned={isOwned} />)}

        {!isLoading && visibleProjects.length === 0 && (
          <Text py="30px" px="16px" textAlign="center" color="gray.400" fontSize="14px">
            {t('hatsuly.projectList.empty')}
          </Text>
        )}
      </Box>

      <ProjectFormModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </Box>
  )
}

export default ProjectTable
